import {
  TRAINING_REPORT_SLUG_CORE,
  TRAINING_REPORT_SLUG_DWB,
  trainingReportPath,
  trainingReportSlugToVariant,
} from './routes'
import {
  VARIANT_K12_DWB,
  VARIANT_K8_CORE,
} from 'components/SiteUserTrainingGrid/constants'
import {
  isAnySitePreferenceEnabled,
  SITE_PROGRAM_ENABLED_PREFERENCE_NAMES,
} from 'lib/util'
import { PROGRAM_OPTION_DWB_LABEL, PROGRAM_OPTION_K8_LABEL } from './constants'

export const DWB_PROGRAM_PREF = 'dwbp_enabled'

const K12_TRAINING_PROGRAM_CODES = ['K8P', 'K12P', 'MSP']

function preferenceIsTrue(value) {
  return value === true || value === 'true'
}

export function siteHasDwbTrainingProgram(site) {
  const pref = site?.preferences?.find(p => p.name === DWB_PROGRAM_PREF)
  return preferenceIsTrue(pref?.value)
}

export function siteHasCoreTrainingProgram(site) {
  return isAnySitePreferenceEnabled(site, [
    SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.k5p,
    SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.msp,
  ])
}

export function getTrainingReportProgramOptions(site) {
  const options = []

  if (siteHasCoreTrainingProgram(site)) {
    options.push({
      value: VARIANT_K8_CORE,
      label: PROGRAM_OPTION_K8_LABEL,
    })
  }

  if (siteHasDwbTrainingProgram(site)) {
    options.push({
      value: VARIANT_K12_DWB,
      label: PROGRAM_OPTION_DWB_LABEL,
    })
  }

  return options
}

export function siteHasK12TrainingAccess(site) {
  return Boolean(
    site?.netSuiteAssets?.some(netSuiteAsset => {
      const programCodes = netSuiteAsset?.product?.programCodes || []
      const hasK12Program = programCodes.some(code =>
        K12_TRAINING_PROGRAM_CODES.includes(code),
      )
      return (
        hasK12Program &&
        netSuiteAsset.isActive &&
        netSuiteAsset?.status !== 'Expired'
      )
    }),
  )
}

export function getSitesWithTrainingAccess(sites) {
  if (!Array.isArray(sites)) return []
  return sites.filter(
    site => siteHasK12TrainingAccess(site) || siteHasDwbTrainingProgram(site),
  )
}

export function isValidTrainingReportSlug(reportSlug) {
  return (
    reportSlug === TRAINING_REPORT_SLUG_CORE ||
    reportSlug === TRAINING_REPORT_SLUG_DWB
  )
}

export function getDefaultSiteForReportSlug(sites, reportSlug) {
  const k12Sites = getSitesWithTrainingAccess(sites)
  if (reportSlug === TRAINING_REPORT_SLUG_DWB) {
    return k12Sites.find(siteHasDwbTrainingProgram) ?? k12Sites[0] ?? null
  }
  return k12Sites[0] ?? null
}

// Effect helpers

export function hasResolvedSiteId(siteId) {
  return Boolean(siteId) && !Number.isNaN(siteId)
}

export function isDwbReportSlug(reportSlug) {
  return (
    reportSlug === TRAINING_REPORT_SLUG_DWB ||
    trainingReportSlugToVariant(reportSlug) === VARIANT_K12_DWB
  )
}

export function shouldSkipInvalidSlugToCoreRedirect(
  isSlugOnlyRoute,
  reportSlug,
) {
  return isSlugOnlyRoute || isValidTrainingReportSlug(reportSlug)
}

export function redirectInvalidSlugToCore({
  history,
  isSlugOnlyRoute,
  reportSlug,
  siteId,
}) {
  if (
    shouldSkipInvalidSlugToCoreRedirect(isSlugOnlyRoute, reportSlug) ||
    !hasResolvedSiteId(siteId)
  ) {
    return
  }
  history.replace(trainingReportPath(TRAINING_REPORT_SLUG_CORE, siteId))
}

export function shouldSkipDwbSlugToCoreRedirect({
  isLoading,
  isSlugOnlyRoute,
  reportSlug,
  site,
}) {
  return (
    isLoading ||
    isSlugOnlyRoute ||
    !site ||
    siteHasDwbTrainingProgram(site) ||
    !isDwbReportSlug(reportSlug)
  )
}

export function redirectDwbSlugToCoreWhenSiteLacksAccess({
  history,
  isLoading,
  isSlugOnlyRoute,
  reportSlug,
  site,
  siteId,
}) {
  if (
    shouldSkipDwbSlugToCoreRedirect({
      isLoading,
      isSlugOnlyRoute,
      reportSlug,
      site,
    })
  ) {
    return
  }
  history.replace(trainingReportPath(TRAINING_REPORT_SLUG_CORE, siteId))
}

export function shouldSkipSlugOnlyDefaultSiteRedirect({
  isLoading,
  isSlugOnlyRoute,
  reportSlug,
}) {
  return !isSlugOnlyRoute || isLoading || !isValidTrainingReportSlug(reportSlug)
}

export function redirectSlugOnlyRouteToDefaultSite({
  history,
  isLoading,
  isSlugOnlyRoute,
  reportSlug,
  sites,
}) {
  if (
    shouldSkipSlugOnlyDefaultSiteRedirect({
      isLoading,
      isSlugOnlyRoute,
      reportSlug,
    })
  ) {
    return
  }
  const defaultSite = getDefaultSiteForReportSlug(sites, reportSlug)
  if (defaultSite) {
    history.replace(trainingReportPath(reportSlug, defaultSite.id))
  }
}

export function shouldSkipSiteTrainingDataLoad({
  isSlugOnlyRoute,
  site,
  siteId,
  siteUsers,
}) {
  return (
    isSlugOnlyRoute || !hasResolvedSiteId(siteId) || !site || Boolean(siteUsers)
  )
}

export function loadSiteTrainingData({
  fetchSiteUsers,
  fetchTrainingCompletion,
  isSlugOnlyRoute,
  site,
  siteId,
  siteUsers,
}) {
  if (
    shouldSkipSiteTrainingDataLoad({
      isSlugOnlyRoute,
      site,
      siteId,
      siteUsers,
    })
  ) {
    return
  }
  fetchSiteUsers(siteId)
  fetchTrainingCompletion(siteId)
}
